import { ol_Feature, ol_format, ol_style, ol_geom, ol_GeometryType } from "@omnia/exports/openlayers"
import { defineComponent, FieldsMap, FieldViewModel, handleApiError, IOmniaShell, LocaleString, map } from "../omnia"
import { IFieldsEditService, IFieldsService } from "../types"
import { InfieldFeaturesCmd } from "./infieldFeaturesCmd"

const msgSaveError = "<p>Could not save the edited field boundary.</p><p>Please check that all of the infield features are contained inside the fields's boundary, and do not overlap or touch this boundary.</p>"

export class IffEditorCtrl extends omnia.shell.Component {

    static create(): ng.IDirective {

        return defineComponent(IffEditorCtrl, "app/fields/infieldFeatures/infieldFeatures.html", {
            fieldsMap: "="
        })

    }

    public fieldsMap: FieldsMap
    public field: FieldViewModel

    private _layer: EditLayer
    private _fieldFeature: any

    private _selectedFeature: FeatureViewModel


    get features(): FeatureViewModel[] {
        if (this._layer) {
            return this._layer.features
        }
    }

    static $inject = ["$scope", "OmniaFieldsService", "OmniaFieldsEditService", "OmniaMapTopologyService", "OmniaShell"]

    constructor($scope: ng.IScope, private svc: IFieldsService, private editSvc: IFieldsEditService, private topo: omnia.map.IMapTopologyService, private shell: IOmniaShell) {

        super($scope, ["vm.fieldsMap"])

        this._icon = "icon-draw"
        this._title = "Manage infield features" as LocaleString
    }

    protected setup() {

        var cmd = <InfieldFeaturesCmd>this.execArguments

        if (!cmd) {
            this.field = null
            return
        }

        this.field = cmd.field

        this.setupMap()

        this.editSvc.startEditing(false, this.field.field)

    }

    protected dispose() {

        this.cleanupMap()

    }

    cancel() {

        this.editSvc.stopEditing()

        this.cleanupMap()

        super.cancel()

    }

    get selectedFeature() {
        return this._selectedFeature
    }

    getFieldDescription(): string {
        if (this.field) return this.field.getDescription(false)
    }

    public hasFeatures(): boolean {
        return this.features && this.features.length > 0
    }

    public isFeatureSelected(f: FeatureViewModel) {
        return this._selectedFeature == f
    }

    public selectFeature(f: FeatureViewModel) {
        this._selectedFeature = f
        this.fieldsMap.commitEdit()
        this.fieldsMap.startEditFeature(f.feature, false)
    }

    private onMapSelectionChanged(feature: any): boolean {
        this._selectedFeature = this.features.find(v => v.feature == feature)
        this.$scope.$applyAsync()
        return true
    }


    private removeSelectedFeature() {

        var f = this._selectedFeature

        if (f) {
            this._selectedFeature = null
            this.features.remove(i => i == f)
            this.fieldsMap.cancelEdit()
            this.fieldsMap.removeFeatures([f.feature])
        }


    }

    private startDrawingFeature(type: ol_GeometryType | "Box" | "Square") {

        var map = this.fieldsMap

        this.fieldsMap.commitEdit()

        map.startDrawingNewFeature(this._layer, type)

    }

    private startPolygon() {
        this.startDrawingFeature("Polygon")
    }

    private startBox() {
        this.startDrawingFeature("Box")
    }

    private startSquare() {
        this.startDrawingFeature("Square")
    }

    private startCircle() {
        this.startDrawingFeature("Circle")
    }


    private setupInitialFeatures(fieldFeature: any) {

        var fieldGeometry = fieldFeature.getGeometry()

        var geometryType = fieldGeometry.getType()

        if (geometryType != "Polygon") {
            throw new Error("Unsupported geometry type: " + geometryType)
        }

        var rings = fieldGeometry.getLinearRingCount()

        var geometries = []

        for (var i = 1; i < rings; i++) {

            var ring = fieldGeometry.getLinearRing(i)
            var coords = ring.getCoordinates()

            var geometry = new ol_geom.Polygon([coords])

            geometries.push(geometry)

        }

        this._layer.setupFeatures(geometries)

    }

    private getResultingGeometry(): any {

        var coordinates = []

        var fieldGeometry = this._fieldFeature.getGeometry()

        var outerRing = fieldGeometry.getLinearRing(0)

        var outerCoordinates = outerRing.getCoordinates()

        var outerGeometry = new ol_geom.Polygon([outerCoordinates])

        coordinates.push(outerCoordinates)

        this.features.forEach(v => {

            var g = v.feature.getGeometry()

            g = this.topo.intersection(g, outerGeometry).getOpenLayersGeometry()

            if (g && (g.getType() == "Polygon")) {

                coordinates.push(g.getLinearRing(0).getCoordinates())

            }

        })

        fieldGeometry = new ol_geom.Polygon(coordinates)

        return fieldGeometry

    }

    private setupMap() {

        var map = this.fieldsMap

        const watcher = (f: FeatureViewModel) => {
            this.$scope.$applyAsync()
            this._selectedFeature = f
        }

        this._layer = new EditLayer(watcher, this.topo.measure)

        FieldLayer.setup(map, this.field)

        map.addLayer(this._layer)

        map.setSelectableLayers([this._layer])

        map.synchronizeSelectionFromMap = false
        map.enableMultipleSelection = false
        map.autoEdit({ layer: this._layer, watcher: f => this.onMapSelectionChanged(f), translate: true })

        var fieldFeature = map.findFeatureWithData(this.field)

        if (fieldFeature) {

            this._fieldFeature = fieldFeature
            map.viewExtent(fieldFeature.getGeometry())

            map.fieldsLayer.setHiddenFeatures([fieldFeature])
            map.croppingLayer.setHiddenFeatures(map.findFeaturesWithData(this.field.croppings))

            this.setupInitialFeatures(fieldFeature)

        }

    }

    private cleanupMap() {

        var map = this.fieldsMap

        map.cancelEdit()

        map.fieldsLayer.setHiddenFeatures(null)
        map.croppingLayer.setHiddenFeatures(null)

        map.removeLayer(this._layer)

        map.clearSelection()

        map.fieldsLayer.clearHighlight()
        map.croppingLayer.clearHighlight()

        map.autoEdit(null)
        map.synchronizeSelectionFromMap = true

        map.resetDefaultLayers(true)

    }
    public commit() {

        var geom = this.getResultingGeometry()
        if (!geom) return

        var wktFormat = new ol_format.WKT()

        var wkt = wktFormat.writeGeometry(geom)

        this.svc.editFieldBoundary(this.field.fieldId, wkt)
            .then(r => this.cancel())
            .catch(r => handleApiError(this.shell, r, { defaultMessage: msgSaveError }))

    }

}

class EditLayer extends omnia.map.Layer {

    private _lastId: number = 0
    features: any[]

    constructor(private newFeatureWatcher: (feature: FeatureViewModel) => void, private measure: omnia.map.IMapMeasuringService) {
        super()
    }

    setupFeatures(geometries: any[]): void {

        var features: FeatureViewModel[] = []

        geometries.forEach(g => {

            var feature = new ol_Feature({ geometry: g })

            var vm = this.setupNewFeature(feature)

            features.push(vm)

        })

        this.features = features

        this._source.addFeatures(features.map(f => f.feature))

        this._source.on("addfeature", e => this.addNewFeature(e.feature))

    }

    private addNewFeature(feature: any): FeatureViewModel {

        var vm = this.setupNewFeature(feature)

        this.features.push(vm)
        this._source.addFeatures([feature])

        this.newFeatureWatcher(vm)

        return vm

    }

    private setupNewFeature(feature: any): FeatureViewModel {

        var id = ++this._lastId

        var vm = new FeatureViewModel(id, feature, this.measure.getArea(feature))

        feature.set("layer", this)
        feature.set("data", vm)
        feature.setId(id)

        return vm

    }



}

export class FeatureViewModel {

    constructor(public id: number, public feature: any, public area: number) {
    }

    getDescription(): string {
        return "Feature #" + this.id + " (" + Math.round(this.area * 100) / 100 + " m²)"
    }

}

export class FieldLayer extends omnia.map.Layer {

    static setup(map: FieldsMap, field: FieldViewModel): FieldLayer {

        var layer = new FieldLayer()

        layer.addFeature(map.findFeatureWithData(field), FieldLayer.getFieldOutlineStyle())

        if (field.croppings.length > 1) {

            var croppingStyle = FieldLayer.getCroppingOutlineStyle()

            field.croppings.forEach(c => {
                layer.addFeature(map.findFeatureWithData(c), croppingStyle)
            })

        }

        map.addLayer(layer)

        return layer

    }

    private addFeature(feature: any, style: any) {

        if (feature) {

            feature = FieldLayer.createOutlineFeature(feature)

            if (feature) {
                if (style) feature.setStyle(style)
                this._source.addFeatures([feature])
            }

        }
    }

    private static getFieldOutlineStyle() {

        return new ol_style.Style({

            stroke: new ol_style.Stroke({
                color: ([220, 20, 60, 1]),
                width: (5)
            }),

        })

    }
    private static getCroppingOutlineStyle() {

        return new ol_style.Style({

            stroke: new ol_style.Stroke({
                color: ([0, 0, 0, 1]),
                width: (1),
                lineDash: [3, 3],
            }),

        })

    }

    private static createOutlineFeature(feature: any) {

        var geometry = feature.getGeometry()

        if (geometry.getLinearRing) {

            var ring = geometry.getLinearRing(0)
            var coordinates = ring.getCoordinates()

            var outlineGeometry = new ol_geom.Polygon([coordinates])

            return new ol_Feature({ geometry: outlineGeometry })

        }
    }

}
